import { isDefined, isUndefined, schAloftArea } from '@aloft/toolkit-shared';
import { colors } from '@aloft/toolkit-sui';

import { Bar, BarChart, CartesianGrid, Legend } from 'recharts';

import { selectOpinionOfSiteValuePrice } from '../../../redux/features/selectors/opinionOfSiteValueSelectors.ts';
import { selectCompSequence } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectSiteEntities } from '../../../redux/features/selectors/propertyDataSelectors/siteSelectors.ts';
import { selectUnadjustedSiteCosts } from '../../../redux/features/selectors/selectUnadjustedSiteCost.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { GraphTitle } from '../components/GraphTitle.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../components/SimpleGraphTooltip.tsx';
import { GraphProps } from '../types.ts';
import { getVacantLandSalesExponentialFit, sortBySiteSize } from './utils.ts';

type SiteExtractionGraphProps = GraphProps & {
  vacantLandSalesExponentialFit: ReturnType<typeof getVacantLandSalesExponentialFit>;
};

type ChartData = {
  propertyName: string;
  siteSize?: number;
  opinionOfSiteValue?: number;
  siteExtraction?: number;
  siteImproved: number;
  vacantLand: number;
};

const valueFormatter = (v?: string | number) => `$${v?.toLocaleString() ?? '-'}`;

export const SiteExtractionGraph = ({
  disableAnimations = false,
  width,
  height,
  vacantLandSalesExponentialFit,
}: SiteExtractionGraphProps) => {
  const compSequence = useAppSelector(selectCompSequence);
  const opinionOfSiteValue = useAppSelector(selectOpinionOfSiteValuePrice)?.toDollarLike() ?? 0;
  const siteExtractions = useAppSelector(selectUnadjustedSiteCosts);

  const sites = useAppSelector(selectSiteEntities);

  const mappedData: ChartData[] = ['subject', ...compSequence]
    .map((id, index) => {
      const isSubject = id === 'subject';
      const siteSizeJSON = sites[id]?.area;
      if (!siteSizeJSON) return;

      const siteExtraction = siteExtractions[id]?.toDollarLike();

      const siteSize = schAloftArea.parse(siteSizeJSON).getUnitAmount('sqft');

      const costPerSF = vacantLandSalesExponentialFit.getCostPerSFFromSiteSize(siteSize);
      const vacantLand = costPerSF * siteSize;

      const siteValue = Math.max(
        isSubject ? opinionOfSiteValue : (siteExtraction ?? 0),
        vacantLand,
      );
      const siteImproved = siteValue - vacantLand;

      return {
        propertyName: isSubject ? 'Subject' : `Comp ${index}`,
        opinionOfSiteValue: isSubject ? opinionOfSiteValue : undefined,
        siteExtraction,
        siteImproved,
        vacantLand,
      };
    })
    .filter(isDefined);

  const data = sortBySiteSize(mappedData);

  return (
    <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
      <BarChart
        data={data}
        width={width}
        height={height}
        margin={{ top: 48, left: 8, bottom: 8, right: 8 }}
        style={{ background: colors.graphBackground, borderRadius: 8 }}
      >
        <GraphTitle>Site Extraction Value</GraphTitle>
        <CartesianGrid vertical={false} />
        <Legend />
        <SimpleGraphTooltip<ChartData>
          disableAnimations={disableAnimations}
          titleKey="propertyName"
          rows={[
            {
              label: 'Opinion of Site Value',
              key: 'opinionOfSiteValue',
              valueFormatter,
              shouldHide: (p) => isUndefined(p.opinionOfSiteValue),
            },
            {
              label: 'Site Extraction',
              key: 'siteExtraction',
              valueFormatter,
              shouldHide: (p) => isUndefined(p.siteExtraction),
            },
            {
              label: 'Site Improved',
              key: 'siteImproved',
              color: 'graphChart',
              valueFormatter,
            },
            {
              label: 'Vacant Land',
              key: 'vacantLand',
              color: 'graphChart3',
              valueFormatter,
            },
          ]}
        />
        <GraphXAxis dataKey="propertyName" />
        <GraphYAxis label="Value ($)" tickFormatter={(val: number) => `$${val.toLocaleString()}`} />

        <Bar
          name="Vacant Land Value"
          dataKey="vacantLand"
          stackId="stack"
          fill={colors.graphChart3}
          isAnimationActive={!disableAnimations}
        />
        <Bar
          name="Site Improved Value"
          dataKey="siteImproved"
          stackId="stack"
          fill={colors.graphChart}
          isAnimationActive={!disableAnimations}
        />
      </BarChart>
    </ResponsiveGraphWrapper>
  );
};
